import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { localMinutes, ritualStreak, seasonKeyFor, type ProgressionState } from '@selflevel/domain';
import { db, type Habit, type LifeArea, type Settings, type Task } from './db';
import {
  buildHeatmap,
  loadHeatCounts,
  loadToday,
  type HeatDay,
  type TodayHabit,
} from './selectors';
import { conectarProgresion, leerProgresion, suscribirProgresion } from './progresion-compartida';
import { DEFAULT_SETTINGS, today as logicalToday } from './session';
import { fijarInicioDelDia, suscribirReloj } from './reloj';

/**
 * Consultas reactivas.
 *
 * `useLiveQuery` vuelve a ejecutar la consulta cuando cambia cualquier tabla que
 * haya tocado. Es lo único que este proyecto necesitaba de RxDB, y aquí son
 * ~25 kB en vez de ~120.
 *
 * Todas devuelven `undefined` durante el primer tic. No se inventa un valor por
 * defecto: pintar un 0 mientras carga y luego saltar a 47 se ve como un error,
 * aunque no lo sea.
 */

export function useSettings(): Settings {
  return useLiveQuery(
    // Lo guardado antes de que existiera un ajuste no lo trae: va el de partida.
    async () => ({ ...DEFAULT_SETTINGS, ...(await db.settings.get('unica')) }),
    [],
    DEFAULT_SETTINGS,
  );
}

/**
 * Hoy, y que SE ENTERE de que cambió. Se recalcula al empezar el día lógico
 * siguiente, al volver a la pestaña y al recuperar el foco (ver `reloj.ts`): una
 * PWA abierta toda la noche ya no amanece en ayer.
 */
export function useToday(): string {
  const settings = useSettings();
  const inicio = settings.dayStartOffsetMin;
  useEffect(() => fijarInicioDelDia(inicio), [inicio]);
  return useSyncExternalStore(suscribirReloj, () => logicalToday(settings));
}

export function useAreas(): LifeArea[] | undefined {
  return useLiveQuery(async () =>
    (await db.lifeAreas.toArray())
      .filter((a) => !a.archivedAt)
      .sort((a, b) => a.sortOrder - b.sortOrder),
  );
}

/** Todas las áreas, también las archivadas: la pantalla de áreas las recupera. */
export function useTodasLasAreas(): LifeArea[] | undefined {
  return useLiveQuery(async () =>
    (await db.lifeAreas.toArray()).sort((a, b) => a.sortOrder - b.sortOrder),
  );
}

/**
 * El mapa de áreas, ESTABLE entre renders.
 *
 * Construía un `Map` nuevo en cada render, así que su identidad cambiaba aunque
 * no hubiera cambiado nada. Cualquier consumidor que lo metiera en unas
 * dependencias —de un efecto, de un `useMemo`— se re-ejecutaba en cada render,
 * y en `Acontecimientos` eso ya produjo un bucle, que se parcheó allí leyendo
 * las áreas de la base en vez de arreglarlo aquí. El arreglo es este: el array
 * de `useLiveQuery` conserva su referencia hasta que la consulta emite de
 * nuevo, así que el mapa solo se reconstruye cuando las áreas cambian de
 * verdad.
 */
export function useAreaMap(): Map<string, LifeArea> {
  const areas = useAreas();
  return useMemo(() => new Map((areas ?? []).map((a) => [a.id, a])), [areas]);
}

export function useHabits(includeArchived = false): Habit[] | undefined {
  return useLiveQuery(
    async () =>
      (await db.habits.toArray())
        .filter((h) => includeArchived || !h.archivedAt)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [includeArchived],
  );
}

export function useTodayHabits(): TodayHabit[] | undefined {
  const today = useToday();
  return useLiveQuery(() => loadToday(today), [today]);
}

/**
 * La progresión. UNA consulta para toda la app, no una por componente: ver
 * `progresion-compartida.ts` para el porqué, que es que se calculaba dos o tres
 * veces por cada casilla marcada.
 */
export function useProgression(): ProgressionState | undefined {
  const today = useToday();
  // El suelo de nivel ya no entra por aquí: lo lee la consulta compartida, que
  // junta el de los ajustes, el del ledger del servidor y el último visto.
  useEffect(() => {
    conectarProgresion(today);
  }, [today]);
  return useSyncExternalStore(suscribirProgresion, leerProgresion);
}

/**
 * El mapa de calor. Los puntos de cada día salen del `byDay` de la progresión
 * compartida, que es exactamente el mismo mapa —`byDay` no depende del día de
 * hoy, ni del suelo, ni de los hitos sellados: es el reparto de puntos por día
 * con sus topes—. Antes se recalculaba la historia entera solo para leerlo.
 * Aquí solo se consulta lo que falta: cuántos registros hubo en el tramo.
 */
export function useHeatmap(from: string, to: string): HeatDay[] | undefined {
  const progression = useProgression();
  const recuentos = useLiveQuery(() => loadHeatCounts(from, to), [from, to]);
  return useMemo(
    () =>
      progression && recuentos ? buildHeatmap(from, to, progression.byDay, recuentos) : undefined,
    [from, to, progression, recuentos],
  );
}

export function useSealedSeasons() {
  return useLiveQuery(async () =>
    (await db.seasons.toArray()).sort((a, b) => (a.key < b.key ? -1 : 1)),
  );
}

export function useMilestoneUnlocks() {
  return useLiveQuery(async () => (await db.milestones.toArray()).sort((a, b) => a.n - b.n));
}

export function useGoals() {
  return useLiveQuery(async () => db.goals.toArray());
}

/** Todos los hitos de meta: el ritual y el tablero solo cuentan «2/5». */
export function useGoalMilestones() {
  return useLiveQuery(async () => db.goalMilestones.toArray());
}

export function useIdentities() {
  return useLiveQuery(async () => (await db.identities.toArray()).filter((i) => !i.retiredAt));
}

/**
 * La entrada del diario de un día, CON el día al que pertenece. `useLiveQuery`
 * devuelve el resultado anterior mientras carga el nuevo: sin la fecha, el editor
 * del día nuevo nacía con el texto del anterior y el autoguardado lo copiaba.
 */
export function useJournal(localDate: string) {
  return useLiveQuery(
    async () => ({
      fecha: localDate,
      entrada: (await db.journalEntries.where('localDate').equals(localDate).first()) ?? null,
    }),
    [localDate],
  );
}

export function useMood(localDate: string) {
  return useLiveQuery(
    async () => (await db.moodLogs.where('localDate').equals(localDate).first()) ?? null,
    [localDate],
  );
}

export function useFocusSessions(limit = 50) {
  return useLiveQuery(
    async () => await db.focusSessions.orderBy('startedAt').reverse().limit(limit).toArray(),
    [limit],
  );
}

export function useOutboxCount(): number {
  return useLiveQuery(
    async () => db.outbox.where('state').anyOf('pending', 'sending').count(),
    [],
    0,
  );
}

export function useCurrentSeasonKey(): string {
  return seasonKeyFor(useToday());
}

/** Si el alta está hecha. Decide entre el alta y la app. */
export function useOnboarded(): boolean | undefined {
  return useLiveQuery(async () => {
    const settings = await db.settings.get('unica');
    if (!settings) return false;
    if (settings.onboardedAt) return true;
    // Si ya hay hábitos, el alta está hecha aunque falte la marca: nunca se
    // devuelve a alguien al alta por un fallo de escritura.
    return (await db.habits.count()) > 0;
  });
}

const porOrden = (a: Task, b: Task): number =>
  a.sortOrder - b.sortOrder || (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0);

export function useTasksOn(localDate: string) {
  return useLiveQuery(
    async () =>
      (await db.tasks.where('plannedDate').equals(localDate).toArray())
        .filter((t) => !t.deletedAt)
        .sort(porOrden),
    [localDate],
  );
}

export function useTasksBetween(from: string, to: string) {
  return useLiveQuery(
    async () =>
      (await db.tasks.where('plannedDate').between(from, to, true, true).toArray())
        .filter((t) => !t.deletedAt)
        .sort(porOrden),
    [from, to],
  );
}

export function useUnplannedTasks() {
  return useLiveQuery(async () =>
    (await db.tasks.filter((t) => t.plannedDate === null && !t.deletedAt).toArray()).sort(porOrden),
  );
}

export function useDayPlan(localDate: string) {
  return useLiveQuery(
    async () => (await db.dayPlans.where('localDate').equals(localDate).first()) ?? null,
    [localDate],
  );
}

export function useDayPlansBetween(from: string, to: string) {
  return useLiveQuery(
    async () => await db.dayPlans.where('localDate').between(from, to, true, true).toArray(),
    [from, to],
  );
}

export function useWeekPlan(weekKey: string) {
  return useLiveQuery(
    async () => (await db.weekPlans.where('weekKey').equals(weekKey).first()) ?? null,
    [weekKey],
  );
}

export function useRitualStreak(today: string): number {
  return (
    useLiveQuery(async () => {
      const sellados = (await db.dayPlans.toArray())
        .filter((p) => p.ritualSealedAt !== null && !p.deletedAt)
        .map((p) => p.localDate);
      return ritualStreak(sellados, today);
    }, [today]) ?? 0
  );
}

function suscribirMinuto(aviso: () => void): () => void {
  const id = setInterval(aviso, 60_000);
  return () => clearInterval(id);
}

/** Minutos de reloj en la zona del usuario; se refresca cada minuto. */
export function useMinutosLocales(tz: string): number {
  return useSyncExternalStore(suscribirMinuto, () => localMinutes(new Date(), tz));
}

/**
 * La cuenta vinculada a este dispositivo, en vivo: `undefined` mientras se lee y
 * `null` sin cuenta. Es la condición de la puerta, no la sesión del servidor: ya
 * dentro, la app sigue funcionando sin conexión aunque la sesión caduque.
 */
export function useCuentaVinculada(): string | null | undefined {
  return useLiveQuery(async () => {
    const fila = await db.meta.get('syncAccountId');
    return typeof fila?.value === 'string' && fila.value.length > 0 ? fila.value : null;
  });
}
